/**
 * CSV export mutation for 患者情報検索 — triggers browser file download.
 * Unlike print mutations (which open in a new tab), CSV uses a synthetic <a download>
 * click so the browser never navigates.
 */

import { useMutation } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { CSV_ERROR_MESSAGE, CSV_SUCCESS_MESSAGE } from '@/constants/messages';
import { exportSearchPatientCsv } from '@/services/main/searchPatientList';
import {
  isServerError,
  showServerErrorModal,
} from '@/pages/main/searchPatientList/serverErrorModal';
import type { PrintSearchPatientPayload } from '@/pages/main/searchPatientList/types';

function formatDateYmd(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

export function useExportSearchPatientCsvMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const appModal = useAppModal();

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    PrintSearchPatientPayload
  >({
    mutationFn: payload => exportSearchPatientCsv(tenant, clinicNumber, payload),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: err => {
      if (isServerError(err)) {
        showServerErrorModal(appModal.warning);
        return;
      }
      toast.error(CSV_ERROR_MESSAGE);
    },
  });

  const triggerExport = async (payload: PrintSearchPatientPayload) => {
    try {
      const blob = await mutateAsync(payload);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `promotion_list_${formatDateYmd()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      // onError already surfaced the toast; swallow here so the UI stays clean.
    }
  };

  return { triggerExport, isLoading };
}
